import chalk from 'chalk';
import fs from 'fs';
import path from 'path';

import { CopyFileProps, FileOperation, Formatter } from './Formatter';
import { MetroParser } from './MetroParser';
import { PodfileTracer } from './utils/PodfileTracer';
import { parsePodfileLock, PodfileLock } from './utils/parsePodfileLock';
import { ERROR } from './utils/symbols';

export type ExpoRunFormatterProps = {
  projectRoot: string;
  podfile?: Record<string, Record<string, string>>;
  appName?: string;
  isDebug?: boolean;
};

/**
 * A superset of `Formatter` which adds support for Metro build errors and cleaner formatting for Node projects.
 */
export class ExpoRunFormatter extends Formatter {
  static create(
    projectRoot: string,
    {
      xcodeProject,
      isDebug,
    }: { xcodeProject?: { name: string } } & Pick<ExpoRunFormatterProps, 'isDebug'> = {}
  ) {
    const appName = xcodeProject?.name.match(/.*\/(.*)\.\w+/)?.[1] || '';
    const formatter = new ExpoRunFormatter({
      projectRoot,
      appName,
      isDebug,
    });

    return formatter;
  }

  private podfileTracer: PodfileTracer;

  _parser: MetroParser | undefined;

  get parser(): MetroParser {
    if (this._parser) {
      return this._parser;
    }
    this._parser = new MetroParser(this);
    return this._parser;
  }

  constructor(public props: ExpoRunFormatterProps) {
    super(props);

    let podfile: PodfileLock = {};
    const podfileLock = path.join(props.projectRoot, 'ios', 'Podfile.lock');

    try {
      const podfileContents = fs.readFileSync(podfileLock, 'utf8');
      podfile = parsePodfileLock(podfileContents) ?? {};
    } catch {}

    this.podfileTracer = new PodfileTracer({
      ...props,
      podfile,
    });
  }

  formatMetroAssetCollectionError(errorContents: string): string {
    const results = `\n${chalk.red(
      ERROR +
        // Provide proper attribution.
        'Metro encountered an error:\n' +
        errorContents
    )}\nLearn more: https://docs.expo.dev/build-reference/troubleshooting\n`;
    this.errors.push(results);
    return results;
  }

  shouldShowCompileWarning(filePath: string, lineNumber?: string, columnNumber?: string): boolean {
    if (this.props.isDebug) {
      return true;
    }
    return (
      !filePath.match(/node_modules/) &&
      !filePath.match(/\/ios\/Pods\//) &&
      // Don't show warnings in the generated build folder.
      !Formatter.getAppRoot(filePath)
    );
  }

  shouldShowLinkerWarning(
    methodName: string,
    collisions: { filePath: string; name: string }[]
  ): boolean {
    if (this.props.isDebug) {
      return true;
    }

    return (
      // Don't show warnings from cocoapods that come from non-root target pods.
      !collisions.some(({ filePath }) => {
        const nodeModule = this.podfileTracer.getNodeModuleNameForTarget(path.dirname(filePath));
        return !nodeModule?.isRootTarget;
      })
    );
  }

  shouldShowWarningInTarget({ target }: { target?: string }): boolean {
    if (this.props.isDebug || !target) {
      return true;
    }

    const nodeModule = this.podfileTracer.getNodeModuleNameForTarget(target);
    return !nodeModule || nodeModule.isRootTarget;
  }

  getNodeModuleName(filePath: string, target?: string): string | null {
    const results = this.podfileTracer.getNodeModuleName(filePath, target);
    return results?.name ? chalk.cyan(results.name) : null;
  }

  formatFileOperation(props: FileOperation): string {
    const title = this.getFileOperationTitle(props.type);
    const moduleNameTag = this.getNodeModuleName(props.filePath, props.target);
    return Formatter.format(
      title,
      [moduleNameTag, Formatter.formatBreadCrumb(props.fileName, props.target, props.project)]
        .filter(Boolean)
        .join(' ')
    );
  }

  formatCopy({ from, to, target }: CopyFileProps): string {
    let relativeFile = Formatter.relativePath(this.props.projectRoot, from);
    // If the relative file reaches outside of the project root, we
    // should attempt to resolve relative to the app output directory.
    if (relativeFile.startsWith('../../')) {
      const appFileRoot = Formatter.getAppRoot(from);
      relativeFile = Formatter.highlightLastPathComponent(
        Formatter.relativePath(appFileRoot, from)
      );
    }
    const appFileRoot = Formatter.getAppRoot(to);
    const relativeAppFile = Formatter.highlightLastPathComponent(
      Formatter.relativePath(appFileRoot, to)
    );
    const moduleNameTag = this.getNodeModuleName('', target);
    return Formatter.format(
      'Copying  ',
      [moduleNameTag, [relativeFile, relativeAppFile].join(' ➜ ')].filter(Boolean).join(' ')
    );
  }

  formatPhaseScriptExecution(scriptName: string, target?: string, project?: string): string {
    const moduleNameTag = this.getNodeModuleName('', target);

    if (scriptName === 'Start Packager') {
      const port = process.env.RCT_METRO_PORT || '8081';
      const isDisabled = isTruthy(process.env.RCT_NO_LAUNCH_PACKAGER);
      const status = isDisabled ? 'disabled' : `http://localhost:${port}`;
      // Add some insight into if the bundler was started or not, and which port was used.
      if (isDisabled) {
        scriptName = chalk.gray(`${scriptName} (disabled)`);
      } else {
        scriptName = scriptName + chalk.gray(` on ${status}`);
      }
    }

    return Formatter.format(
      'Executing',
      [moduleNameTag, Formatter.formatBreadCrumb(scriptName, target, project)]
        .filter(Boolean)
        .join(' ')
    );
  }
}

function isTruthy(value: any): boolean {
  return value === 'true' || value === 1 || value === '1';
}
